import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { isMeasurementStarted, Measurement, MEASUREMENT_TYPE_DISPLAY_CONFIG, STAGE_OPEN_GESTURE } from '@cosmos/shared';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { MeasurementLivenessBadgeComponent } from '../../components/measurement-liveness-badge/measurement-liveness-badge.component';

type MeasurementCardStatus = 'started' | 'awaiting-start' | 'never-started' | 'finished';

@Component({
  selector: 'cosmos-measurement-card',
  imports: [
    TranslatePipe,
    CardModule,
    ButtonModule,
    TagModule,
    RouterModule,
    DatePipe,
    DecimalPipe,
    MeasurementLivenessBadgeComponent,
  ],
  templateUrl: './measurement-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .measurement-card ::ng-deep .p-card {
      box-shadow: var(--p-card-shadow);
      transition: box-shadow 150ms ease;
    }
    .measurement-card--selectable:hover ::ng-deep .p-card {
      box-shadow: var(--p-card-shadow), 0 0 0 1px var(--p-surface-300);
    }
    .measurement-card--selected ::ng-deep .p-card {
      box-shadow: 0 0 0 2px var(--p-primary-color) !important;
    }
  `,
})
export class MeasurementCardComponent {
  readonly measurement = input.required<Measurement>();

  /** When true, the card participates in the stage browser's select/open
   *  gesture (STAGE_OPEN_GESTURE) — single-click selects, double-click opens
   *  in 'double' mode; single-click opens in 'single' mode. When false
   *  (the default, e.g. the stage-view measurement grid before this) the
   *  card is inert and only its footer "Open" button navigates. */
  readonly selectable = input<boolean>(false);
  readonly selected = input<boolean>(false);

  readonly open = output<void>();
  readonly select = output<void>();

  protected openGesture: 'single' | 'double' = STAGE_OPEN_GESTURE;

  readonly status = computed<MeasurementCardStatus>(() => {
    const m = this.measurement();
    const started = isMeasurementStarted(m);
    if (started) return m.active ? 'started' : 'finished';
    return m.active ? 'awaiting-start' : 'never-started';
  });

  readonly statusSeverity = computed(() => {
    switch (this.status()) {
      case 'started': return 'success';
      case 'awaiting-start': return 'warn';
      case 'never-started': return 'secondary';
      case 'finished': return 'info';
    }
  });

  readonly displayConfig = computed(() => {
    const m = this.measurement();
    return m.measurementType ? MEASUREMENT_TYPE_DISPLAY_CONFIG[m.measurementType] ?? null : null;
  });

  readonly primaryValue = computed(() => {
    const m = this.measurement();
    const config = this.displayConfig();
    if (!config || this.status() === 'awaiting-start' || this.status() === 'never-started') return null;
    const series = (m as any)[config.primaryVariable] as number[] | undefined;
    return series?.length ? series[series.length - 1] : null;
  });

  readonly lastTimestamp = computed(() => {
    const t = this.measurement().time;
    return t?.length ? t[t.length - 1] : null;
  });

  readonly showLiveness = computed(() => {
    const m = this.measurement();
    return !!m.active && !!m.measurementType && m.measurementType in MEASUREMENT_TYPE_DISPLAY_CONFIG;
  });

  /** Non-optional accessor for template use — only read when `showLiveness()` is true, which already guarantees `measurementType` is set. */
  readonly measurementType = computed(() => this.measurement().measurementType ?? '');

  readonly deviceName = computed(() => {
    const device = this.measurement().device as any;
    return device && typeof device === 'object' ? device.name : null;
  });

  onClick(): void {
    if (!this.selectable()) return;
    if (this.openGesture === 'single') this.open.emit();
    else this.select.emit();
  }

  onDoubleClick(): void {
    if (!this.selectable()) return;
    if (this.openGesture === 'double') this.open.emit();
  }
}
